import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import ErrorDialog from '../components/ui/ErrorDialog';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import DocsContent from '../components/DocsContent';
import ApiCodeContent from '../components/ApiCodeContent';
import IntroContent from '../components/IntroContent';
import { toast } from 'react-hot-toast';
import { Copy, Trash2, User, Users } from 'lucide-react';
import { api } from '../services/authService';
import { organizationsService } from '../services/organizationsService';
import { PERMISSIONS, hasPermission } from '../services/permissions';

const API = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedName, setSelectedName] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiKeysLoading, setApiKeysLoading] = useState(true);
  const [showNewKey, setShowNewKey] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState('');
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('intro');

  // Format email address into a display name (same logic as Dashboard)
  const formatNameFromEmail = (email) => {
    if (!email) return '';
    // Get the part before '@'
    const localPart = email.split('@')[0];
    // Split on '.' and capitalize first letter of each part
    return localPart
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  useEffect(() => {
    // Fetch all users if current user is admin
    const fetchUsers = async () => {
      if (!currentUser?.is_admin) return;
      
      try {
        const res = await api.get('/api/v2/users/');
        const data = res.data;
        setUsers(data.filter(u => u.is_active)); // Only show active users
      } catch (e) {
        setErrorMessage('Failed to load users. Please refresh the page and try again.');
        setShowErrorDialog(true);
      }
    };
    fetchUsers();
  }, [currentUser?.is_admin]);

  useEffect(() => {
    const load = async (retryCount = 0) => {
      const maxRetries = 3;
      const retryDelay = 1000; // 1 second
      
      try {
        setApiKeysLoading(true);
        
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
        
        try {
          // Fetch API keys for selected user or current user
          // Note: Admin endpoint is disabled, so we use the regular endpoint
          // The backend will return keys for the authenticated user
          let keysRes;
          if (selectedUser && currentUser?.is_admin) {
            // Admin fetching keys for another user - use admin endpoint
            keysRes = await api.get(`/api/v2/admin/users/${selectedUser.id}/api-keys`, { 
              signal: controller.signal 
            });
          } else {
            // User fetching their own keys
            keysRes = await api.get('/api/v2/api-keys/', { signal: controller.signal });
          }
          
          // Fetch organizations for selected user (if impersonating) or current user
          let orgsRes;
          if (selectedUser && currentUser?.is_admin) {
            // Admin fetching organizations for the selected user
            orgsRes = await api.get(`/api/v2/users/${selectedUser.id}/organizations`, { 
              signal: controller.signal 
            });
          } else {
            // User fetching their own organizations
            orgsRes = await api.get('/api/v2/organizations/', { signal: controller.signal });
          }
          
          clearTimeout(timeoutId);
          
          if (keysRes.status === 200) {
            const keys = keysRes.data;
            setApiKeys(keys);
          } else if (keysRes.status >= 500 && retryCount < maxRetries) {
            // Retry on server errors
            setTimeout(() => load(retryCount + 1), retryDelay * (retryCount + 1));
            return;
          } else if (keysRes.status !== 200) {
            setApiKeys([]);
            if (keysRes.status !== 404) {
              const errorDetail = keysRes.data?.detail || keysRes.data?.message || keysRes.statusText || `HTTP ${keysRes.status}`;
              const impersonationContext = selectedUser && currentUser?.is_admin 
                ? ` (impersonating ${selectedUser.first_name} ${selectedUser.last_name})`
                : '';
              setErrorMessage(`Failed to load API keys${impersonationContext}.\n\nStatus: ${keysRes.status}\nError: ${errorDetail}\n\nPlease refresh the page and try again.`);
              setShowErrorDialog(true);
            }
          }
          
          if (orgsRes.status === 200) {
            // Keys can only be created where the owner's role grants API access; admins
            // impersonating another user see that user's orgs and the backend checks the target.
            const impersonating = Boolean(selectedUser && currentUser?.is_admin);
            const allOrgs = Array.isArray(orgsRes.data) ? orgsRes.data : [];
            const orgs = impersonating
              ? allOrgs
              : allOrgs.filter((o) => hasPermission(currentUser, o.id, PERMISSIONS.API_ACCESS));
            setOrganizations(orgs);
            // The shared org cache feeds every page's pulldown, so it must hold the user's
            // full list — only this page's own pulldown is narrowed to API-access orgs.
            if (!impersonating) {
              organizationsService.setOrganizationsInCookie(
                [...allOrgs].sort((a, b) => a.name.localeCompare(b.name))
              );
            }
            // Set default organization to first item (sorted alphabetically)
            if (orgs.length > 0 && !selectedOrganization) {
              const sortedOrgs = [...orgs].sort((a, b) => a.name.localeCompare(b.name));
              // Try to load from cookie first (only for current user)
              if (!selectedUser || !currentUser?.is_admin) {
                const savedSelected = organizationsService.getSelectedOrganization('id');
                if (savedSelected && orgs.some(o => o.id.toString() === savedSelected)) {
                  setSelectedOrganization(savedSelected);
                  organizationsService.setSelectedOrganization(savedSelected, 'id', sortedOrgs);
                } else {
                  setSelectedOrganization(sortedOrgs[0].id.toString());
                  organizationsService.setSelectedOrganization(sortedOrgs[0].id.toString(), 'id', sortedOrgs);
                }
              } else {
                setSelectedOrganization(sortedOrgs[0].id.toString());
              }
            }
          } else if (orgsRes.status !== 200 && orgsRes.status < 500) {
            setOrganizations([]);
            if (orgsRes.status !== 404) {
              const errorDetail = orgsRes.data?.detail || orgsRes.data?.message || orgsRes.statusText || `HTTP ${orgsRes.status}`;
              const impersonationContext = selectedUser && currentUser?.is_admin 
                ? ` (impersonating ${selectedUser.first_name} ${selectedUser.last_name})`
                : '';
              setErrorMessage(`Failed to load organizations${impersonationContext}.\n\nStatus: ${orgsRes.status}\nError: ${errorDetail}\n\nPlease refresh the page and try again.`);
              setShowErrorDialog(true);
            }
          }
        } catch (fetchError) {
          clearTimeout(timeoutId);
          if (fetchError.name === 'AbortError' || fetchError.code === 'ECONNABORTED' || (fetchError.message && fetchError.message.includes('timeout'))) {
            // Timeout - retry if we haven't exceeded max retries
            if (retryCount < maxRetries) {
              setTimeout(() => load(retryCount + 1), retryDelay * (retryCount + 1));
              return;
            } else {
              const impersonationContext = selectedUser && currentUser?.is_admin 
                ? ` (impersonating ${selectedUser.first_name} ${selectedUser.last_name})`
                : '';
              setErrorMessage(`Request timed out${impersonationContext}.\n\nThe request took longer than 30 seconds to complete.\n\nPlease check your connection and try again.`);
              setShowErrorDialog(true);
            }
          } else if (retryCount < maxRetries && fetchError.response?.status >= 500) {
            // Server error - retry
            setTimeout(() => load(retryCount + 1), retryDelay * (retryCount + 1));
            return;
          } else {
            throw fetchError;
          }
        } finally {
          setApiKeysLoading(false);
        }
      } catch (e) {
        if (retryCount < maxRetries) {
          setTimeout(() => load(retryCount + 1), retryDelay * (retryCount + 1));
        } else {
          setApiKeysLoading(false);
          const impersonationContext = selectedUser && currentUser?.is_admin 
            ? ` (impersonating ${selectedUser.first_name} ${selectedUser.last_name})`
            : '';
          
          let errorDetails = 'An unexpected error occurred.';
          if (e.response) {
            const status = e.response.status;
            const statusText = e.response.statusText || `HTTP ${status}`;
            const errorDetail = e.response.data?.detail || e.response.data?.message || '';
            errorDetails = `Status: ${status} (${statusText})`;
            if (errorDetail) {
              errorDetails += `\nError: ${errorDetail}`;
            }
          } else if (e.message) {
            errorDetails = `Error: ${e.message}`;
          } else if (e.name) {
            errorDetails = `Error: ${e.name}`;
          }
          
          setErrorMessage(`Failed to load data${impersonationContext}.\n\n${errorDetails}\n\nPlease refresh the page and try again.`);
          setShowErrorDialog(true);
        }
      }
    };
    load();
    // Note: selectedOrganization is intentionally not in dependencies to avoid infinite loops
    // It's only set when empty, so adding it would cause unnecessary re-fetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, currentUser?.is_admin]);

  const handleCreateKey = async () => {
    if (!selectedName || !selectedOrganization) {
      setErrorMessage('Please fill in all fields (name and organization) before creating an API key.');
      setShowErrorDialog(true);
      return;
    }
    
    // Validate that name only contains alphanumeric, underscores, and hyphens
    if (!/^[a-zA-Z0-9_-]+$/.test(selectedName)) {
      setErrorMessage('API key name can only contain letters, numbers, underscores (_), and hyphens (-).');
      setShowErrorDialog(true);
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        name: selectedName,  // Name will be used to create key name
        organization_id: selectedOrganization  // Keep as string (UUID) - don't parse as int
      };
      
      // Use admin endpoint when impersonating
      const url = isImpersonating && currentUser?.is_admin
        ? `/api/v2/admin/users/${selectedUser.id}/api-keys`
        : '/api/v2/api-keys/';
      
      const res = await api.post(url, payload);
      const created = res.data;
      setApiKeys([...apiKeys, created]);
      if (created.key) {
        setNewKeyValue(created.key);
        setShowNewKey(true);
      }
      setSelectedName('');
      setSelectedOrganization('');
      
      const userText = isImpersonating 
        ? ` for ${formatNameFromEmail(selectedUser?.email) || selectedUser?.first_name || 'user'}` 
        : '';
      toast.success(`API key created successfully${userText}`);
    } catch (error) {
      console.error('Error creating API key:', error);
      
      const impersonationContext = isImpersonating && selectedUser
        ? ` (impersonating ${selectedUser.first_name} ${selectedUser.last_name})`
        : '';
      
      let errorDetails = 'Failed to create API key.';
      if (error.response) {
        const status = error.response.status;
        const statusText = error.response.statusText || `HTTP ${status}`;
        const errorDetail = error.response.data?.detail || error.response.data?.message || '';
        errorDetails = `Status: ${status} (${statusText})`;
        if (errorDetail) {
          errorDetails += `\nError: ${errorDetail}`;
        }
        if (status === 422) {
          errorDetails += '\n\nThis usually means there was a validation error. Please check that all fields are filled correctly.';
        } else if (status === 403) {
          errorDetails += '\n\nYou may not have permission to create API keys for this user.';
        }
      } else if (error.message) {
        errorDetails = `Error: ${error.message}`;
      }
      
      setErrorMessage(`Failed to create API key${impersonationContext}.\n\n${errorDetails}\n\nPlease check the details and try again.`);
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKey = async (key) => {
    setKeyToDelete(key);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!keyToDelete) return;
    
    setLoading(true);
    try {
      // Use admin endpoint when impersonating
      const url = isImpersonating && selectedUser && currentUser?.is_admin
        ? `/api/v2/admin/users/${selectedUser.id}/api-keys/${keyToDelete.id}`
        : `/api/v2/api-keys/${keyToDelete.id}`;
      
      await api.delete(url);
      setApiKeys(apiKeys.filter(k => k.id !== keyToDelete.id));
      const userText = isImpersonating ? ` for ${selectedUser.first_name}` : '';
      toast.success(`API key deleted successfully${userText}`);
      setKeyToDelete(null);
    } catch (error) {
      const impersonationContext = isImpersonating && selectedUser
        ? ` (impersonating ${selectedUser.first_name} ${selectedUser.last_name})`
        : '';
      
      let errorDetails = 'Failed to delete API key.';
      if (error.response) {
        const status = error.response.status;
        const statusText = error.response.statusText || `HTTP ${status}`;
        const errorDetail = error.response.data?.detail || error.response.data?.message || '';
        errorDetails = `Status: ${status} (${statusText})`;
        if (errorDetail) {
          errorDetails += `\nError: ${errorDetail}`;
        }
        if (status === 404) {
          errorDetails += '\n\nThe API key may have already been deleted.';
        } else if (status === 403) {
          errorDetails += '\n\nYou may not have permission to delete API keys for this user.';
        }
      } else if (error.message) {
        errorDetails = `Error: ${error.message}`;
      }
      
      setErrorMessage(`Failed to delete API key${impersonationContext}.\n\n${errorDetails}\n\nPlease try again.`);
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
      setKeyToDelete(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const resetToCurrentUser = () => {
    setSelectedUser(null);
    setIsImpersonating(false);
    setSelectedName('');
    setSelectedOrganization('');
    setShowNewKey(false);
    setNewKeyValue('');
  };

  const isSwaggerTab = activeTab === 'swagger';

  return (
    <div
      className={
        isSwaggerTab
          ? // Fill the height under the app header so only the iframe scrolls.
            'flex flex-col -my-8 h-[calc(100vh-4rem)] overflow-hidden'
          : 'pb-6 space-y-6'
      }
    >
      {/* Tabs */}
      <div
        className={`flex space-x-2 border-b flex-shrink-0 ${
          isSwaggerTab ? 'pt-4 bg-white' : ''
        }`}
      >
        <button
          type="button"
          onClick={() => setActiveTab('intro')}
          className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'intro' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600'}`}
        >
          Intro
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('api-keys')}
          className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'api-keys' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600'}`}
        >
          API Keys
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('code')}
          className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'code' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600'}`}
        >
          Code
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('swagger')}
          className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'swagger' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600'}`}
        >
          Swagger
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('docs')}
          className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'docs' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600'}`}
        >
          Docs
        </button>
      </div>

      {activeTab === 'intro' ? (
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <IntroContent />
          </div>
        </div>
      ) : activeTab === 'docs' ? (
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <DocsContent />
          </div>
        </div>
      ) : activeTab === 'code' ? (
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <ApiCodeContent />
          </div>
        </div>
      ) : activeTab === 'swagger' ? (
        <div className="flex-1 min-h-0 overflow-hidden bg-white">
          <iframe
            title="Swagger UI"
            src="/swagger"
            className="w-full h-full border-0 bg-white"
          />
        </div>
      ) : (
        <>
      {/* User Impersonation (Admin Only) */}
      {currentUser?.is_admin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2" />
              User Impersonation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <FunctionalSelect
                label="User"
                value={selectedUser?.id?.toString() || ''}
                onValueChange={(value) => {
                  if (value === '') {
                    resetToCurrentUser();
                  } else {
                    const user = users.find(u => u.id.toString() === value);
                    setSelectedUser(user);
                    setIsImpersonating(true);
                  }
                }}
                placeholder="Select user to manage their API keys"
              >
                <SelectItem value="">
                  {currentUser?.first_name && currentUser?.last_name 
                    ? `${currentUser.first_name} ${currentUser.last_name} (Current User)`
                    : currentUser?.email 
                    ? `${formatNameFromEmail(currentUser.email)} (Current User)`
                    : 'Current User'
                  }
                </SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id.toString()}>
                    {user.first_name} {user.last_name} ({user.email})
                    {user.is_admin && ' - Admin'}
                  </SelectItem>
                ))}
              </FunctionalSelect>
              <p className="text-xs text-gray-600">
                💡 Select a user to create, view, and manage their API keys. This allows you to help users with their API key setup.
              </p>
            </div>
            
            {isImpersonating && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-blue-800 font-medium">
                      🔄 Managing API Keys for: <strong>
                        {selectedUser?.first_name && selectedUser?.last_name
                          ? `${selectedUser.first_name} ${selectedUser.last_name}`
                          : formatNameFromEmail(selectedUser?.email) || 'User'
                        }
                      </strong>
                    </p>
                    <p className="text-sm text-blue-700 mt-1">
                      Email: {selectedUser?.email || 'N/A'}
                    </p>
                    <p className="text-sm text-blue-700">
                      Status: <span className={`px-2 py-1 rounded-full text-xs ${
                        selectedUser.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedUser.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {selectedUser.is_admin && (
                        <span className="ml-2 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          Admin
                        </span>
                      )}
                    </p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={resetToCurrentUser}
                  >
                    <User className="h-4 w-4 mr-2" />
                    Return to Current User
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Create New API Key */}
      <Card>
        <CardHeader>
          <CardTitle>
            {isImpersonating 
              ? `Create API Key for ${formatNameFromEmail(selectedUser?.email) || selectedUser?.first_name || 'User'}` 
              : 'Create New API Key'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Context */}
          {isImpersonating && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <p className="text-sm text-gray-700">
                <strong>Context:</strong> You are creating an API key for <strong>
                  {selectedUser?.first_name && selectedUser?.last_name
                    ? `${selectedUser.first_name} ${selectedUser.last_name}`
                    : formatNameFromEmail(selectedUser?.email) || 'User'
                  }
                </strong>. 
                The key will be associated with their account and they will have full control over it.
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <FunctionalSelect
                label="Organization"
                value={organizationsService.getSelectedOptionValue([...organizations].sort((a, b) => a.name.localeCompare(b.name)), selectedOrganization, 'id')}
                onValueChange={(value) => {
                  const parsed = organizationsService.parseOrganizationOptionValue(value);
                  if (parsed) {
                    setSelectedOrganization(parsed.id);
                    if (!selectedUser || !currentUser?.is_admin) {
                      organizationsService.setSelectedOrganization(parsed.id, 'id', [{ name: parsed.name, id: parsed.id }]);
                    }
                  } else {
                    setSelectedOrganization(value);
                    if (!selectedUser || !currentUser?.is_admin) {
                      organizationsService.setSelectedOrganization(value, 'id', organizations);
                    }
                  }
                }}
                placeholder="Select organization"
              >
                {[...organizations].sort((a, b) => a.name.localeCompare(b.name)).map((org) => (
                  <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                    {org.name}
                  </SelectItem>
                ))}
              </FunctionalSelect>
            </div>
            <div className="space-y-2">
              <Input
                id="name"
                type="text"
                value={selectedName}
                onChange={(e) => {
                  // Only allow alphanumeric, underscores, and hyphens
                  const sanitized = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
                  setSelectedName(sanitized);
                }}
                placeholder="Enter API key name"
                aria-label="Name"
              />
              <p className="text-xs text-gray-500">
                Only letters, numbers, underscores (_), and hyphens (-) are allowed
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={handleCreateKey} disabled={loading}>
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </CardContent>
      </Card>

      {/* New Key Display */}
      {showNewKey && newKeyValue && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-800">
              {isImpersonating 
                ? `New API Key Created for ${formatNameFromEmail(selectedUser?.email) || selectedUser?.first_name || 'User'}` 
                : 'New API Key Created'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-green-700">
                {isImpersonating 
                  ? `API key created for ${formatNameFromEmail(selectedUser?.email) || selectedUser?.first_name || 'user'}. Make sure to share this key with them securely.`
                  : 'Make sure to copy your API key now. You won\'t be able to see it again!'
                }
              </p>
              <div className="flex items-center space-x-2">
                <Input
                  value={newKeyValue}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  size="sm"
                  onClick={() => copyToClipboard(newKeyValue)}
                  className="flex-shrink-0"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setShowNewKey(false);
                  setNewKeyValue('');
                }}
              >
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* API Keys List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {isImpersonating 
              ? `API Keys for ${formatNameFromEmail(selectedUser?.email) || selectedUser?.first_name || 'User'}`
              : `API Keys for ${formatNameFromEmail(currentUser?.email) || currentUser?.first_name || 'You'}`
            }
          </CardTitle>
        </CardHeader>
        <CardContent>
          {apiKeysLoading ? (
            <div className="text-center py-8 text-gray-500">Loading API keys...</div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {isImpersonating 
                  ? `No API keys found for ${formatNameFromEmail(selectedUser?.email) || selectedUser?.first_name || 'user'}`
                  : 'No API keys found'
                }
              </p>
              {isImpersonating && (
                <p className="text-sm text-gray-400 mt-2">
                  You can create API keys for them using the form above.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {apiKeys.map((key) => (
                <div key={key.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="font-medium">
                        {(organizations.find(o => o.id === key.organization_id) || {}).name || `Org #${key.organization_id}`}: {key.name ? key.name.charAt(0).toUpperCase() + key.name.slice(1) : key.name}
                      </h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        key.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {key.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {isImpersonating && (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          {formatNameFromEmail(selectedUser?.email) || selectedUser?.first_name || 'User'}'s key
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      Created: {new Date(key.created_at).toLocaleDateString()}
                      {key.expires_at && ` • Expires: ${new Date(key.expires_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteKey(key)}
                      disabled={loading}
                      style={{ 
                        backgroundColor: '#F53D7A', 
                        borderColor: '#F53D7A',
                        color: '#FFFFFF'
                      }}
                      onMouseEnter={(e) => {
                        if (!loading) e.target.style.backgroundColor = '#E02D6A';
                      }}
                      onMouseLeave={(e) => {
                        if (!loading) e.target.style.backgroundColor = '#F53D7A';
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        message={errorMessage}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setKeyToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete API Key"
        message={keyToDelete ? (() => {
          const organization = organizations.find(o => o.id === keyToDelete.organization_id);
          const orgName = organization?.name || `Org #${keyToDelete.organization_id}`;
          const role = keyToDelete.name ? keyToDelete.name.charAt(0).toUpperCase() + keyToDelete.name.slice(1) : keyToDelete.name;
          return `Are you sure you want to delete API key ${orgName}:${role}?`;
        })() : ''}
      />
        </>
      )}
    </div>
  );
};

export default API;
